// import { useEffect, useState } from 'react';
// import { generateFormErrors, generateInitialFormErrors, getFieldErrors } from './useForm.helpers';
// import { TFormValue, IFormConfig } from './useForm.types';
// const useForm = <T extends Record<string, any>>(formConfig: IFormConfig, initalFormValues: T) => {
//   const [formState, setFormState] = useState(initalFormValues);
//   const [formErrors, setFormErrors] = useState<Record<string, Array<string>>>(generateInitialFormErrors(formConfig));
//   const [isFormValid, setIsFormValid] = useState(false);
//   const updateFormState = (updatedFormState) => {
//     setFormState(updatedFormState);
//   };
//   const handleFieldChange = (fieldName: string, fieldValue: TFormValue) => {
//     const fieldValidation = formConfig[fieldName];
//     setFormState({
//       ...formState,
//       [fieldName]: fieldValue,
//     });
//     const fieldErrors = getFieldErrors(fieldValidation, fieldValue);
//     setFormErrors({
//       ...formErrors,
//       [fieldName]: fieldErrors as Array<string>,
//     });
//   };
//   const validateForm = () => {
//     const updatedErrors = generateFormErrors(formConfig, formState);
//     setFormErrors(updatedErrors);
//     const formHasErrors = Object.keys(updatedErrors).some(
//       (fieldName) => updatedErrors[fieldName as keyof typeof updatedErrors].length,
//     );
//     setIsFormValid(!formHasErrors);
//     return !formHasErrors;
//   };
//   const hasFormChanges = JSON.stringify(initalFormValues) !== JSON.stringify(formState);
//   const getChangedFields = (): T | {} => {
//     return Object.keys(formState).reduce((updatedFeilds, key) => {
//       if (formState[key] !== initalFormValues[key]) {
//         return { ...updatedFeilds, [key]: formState[key] };
//       }
//       return updatedFeilds;
//     }, {});
//   };
//   return {
//     formState,
//     formErrors,
//     handleFieldChange,
//     validateForm,
//     isFormValid,
//     getIsFormValid: () => isFormValid,
//     getFormState: () => formState,
//     updateFormState,
//     hasFormChanges,
//     getChangedFields,
//   };
// };
// export default useForm;
import { useEffect, useMemo, useRef, useState } from 'react';

import { generateFormErrors, generateInitialFormErrors, getFieldErrors } from './useForm.helpers';
import { TFormValue, IFormConfig } from './useForm.types';

const useForm = <T extends Record<string, any>>(formConfig: IFormConfig, initalFormValues: T) => {
  const initialFormState = useRef(null);
  const [formState, setFormState] = useState(initalFormValues);
  const [formErrors, setFormErrors] = useState<Record<string, Array<string>>>(generateInitialFormErrors(formConfig));

  useEffect(() => {
    initialFormState.current = initalFormValues;
  }, []);

  const updateInitialFormState = (updatedInitialFormState: T) => {
    initialFormState.current = updatedInitialFormState;

    setFormState(updatedInitialFormState);
  };

  const updateFormState = (updatedFormState: T) => {
    setFormState(updatedFormState);
  };

  const handleFieldChange = (fieldName: string, fieldValue: TFormValue) => {
    const fieldValidation = formConfig[fieldName];

    setFormState((currentState) => ({
      ...currentState,
      [fieldName]: fieldValue === '' ? null : fieldValue,
    }));

    const fieldErrors = getFieldErrors(fieldValidation, fieldValue, formState);

    validateForm();

    setFormErrors({
      ...formErrors,
      [fieldName]: fieldErrors as Array<string>,
    });
  };

  const validateForm = () => {
    const updatedErrors = generateFormErrors(formConfig, formState);

    setFormErrors(updatedErrors);

    const formHasErrors = Object.keys(updatedErrors).some(
      (fieldName) => updatedErrors[fieldName as keyof typeof updatedErrors].length,
    );

    return !formHasErrors;
  };

  const hasFormChanges = JSON.stringify(initialFormState.current) !== JSON.stringify(formState);

  const getChangedFields = (): T | {} => {
    return Object.keys(formState).reduce((updatedFeilds, key) => {
      if (formState[key] !== initialFormState.current[key]) {
        return { ...updatedFeilds, [key]: formState[key] };
      }
      return updatedFeilds;
    }, {});
  };

  const resetFormState = () => {
    setFormState(initalFormValues);
  };

  const isFormValid = useMemo(() => !Object.keys(formErrors).some((field) => formErrors[field].length), [formErrors]);

  return {
    formState,
    formErrors,
    handleFieldChange,
    validateForm,
    isFormValid,
    getIsFormValid: () => isFormValid,
    getFormState: () => formState,
    updateFormState,
    hasFormChanges,
    getChangedFields,
    resetFormState,
    updateInitialFormState,
  };
};

export default useForm;
